import {
  type DrillProgress,
  passesPerStage,
  stages,
} from "../handwriting/xiezi-stages.js";
import { pieOf, stageNames, wedgePath } from "./xiezi-pie-shape.js";

/**
 * Where a reader is in the drill, as one small pie per stage.
 *
 * Four pies of five wedges, a wedge filled for each pass done, so that a
 * reader sees how far they have come and how far is left without reading the
 * line over the square. The line says the same in words, and is what a screen
 * reader is given, so the pies are hidden from one.
 *
 * **A pass done flashes its wedge, and a stage done flashes its pie.** The
 * drill says which through {@link XieziPies.show}. In an Exercise the next
 * character comes into the square straight after a pass, at a place of its
 * own, so a flash holds the pies at the pass just done until it has played,
 * and the next character's place is drawn after it.
 *
 * **A wedge fills once the character has flown into it.** `xiezi-flash.ts`
 * sends a green copy of the character from the square into the wedge, and the
 * stylesheet holds the wedge empty until it lands, then flashes it green and
 * rocks the pie. A stage done flashes, grows and turns the whole pie.
 *
 * **Each pie is named over it, in a word.** `xiezi-pie-shape.ts` has the
 * words, with the paths the wedges are drawn along.
 *
 * `modules/cidian-xiezi` colours them. The markup draws an empty container
 * where they go, so the square does not move when they arrive.
 */

/**
 * How long a flash holds the pies: the flight into the wedge and the flash
 * after it. `modules/cidian-xiezi` animates over the same.
 */
export const pieFlashLength = 1400;

const svgNamespace = "http://www.w3.org/2000/svg";

/** One stage's pie, its five wedges drawn and nothing filled. */
function emptyPie(document: Document): SVGElement {
  const pie = document.createElementNS(svgNamespace, "svg");

  pie.setAttribute("viewBox", "-1.1 -1.1 2.2 2.2");
  pie.setAttribute("class", "cidian-xiezi-pie");

  for (let at = 0; at < passesPerStage; at += 1) {
    const wedge = document.createElementNS(svgNamespace, "path");

    wedge.setAttribute("d", wedgePath(at, passesPerStage));
    wedge.setAttribute("class", "cidian-xiezi-wedge");
    pie.append(wedge);
  }

  return pie;
}

/** A pie with its stage's name over it. */
function namedPie(pie: SVGElement, name: string): HTMLElement {
  const document = pie.ownerDocument;
  const item = document.createElement("span");
  const label = document.createElement("span");

  item.className = "cidian-xiezi-pie-item";
  label.className = "cidian-xiezi-pie-name";
  label.textContent = name;
  item.append(label, pie);

  return item;
}

/** The pies in one drill block. */
export class XieziPies {
  readonly #pies: readonly SVGElement[];
  #holding: ReturnType<typeof setTimeout> | undefined;
  #waiting: DrillProgress | undefined;

  /** @param container - The empty element the markup drew for them. */
  constructor(container: HTMLElement) {
    const pies = stages.map(() => emptyPie(container.ownerDocument));

    container.setAttribute("aria-hidden", "true");
    container.replaceChildren(
      ...stages.map((stage, at) => namedPie(pies[at]!, stageNames[stage])),
    );
    this.#pies = pies;
  }

  /**
   * The wedge the pass the reader is on fills once it is done.
   * @param progress - Where the reader is, before the pass is done.
   * @returns The wedge, or nothing once the drill is finished.
   */
  wedge(progress: DrillProgress): Element | undefined {
    return this.#pies[progress.stage]?.children[progress.pass];
  }

  /**
   * Draw where the reader is.
   *
   * @param progress - Where they are.
   * @param done - What the move that brought them here finished, if anything,
   * which flashes: a pass flashes the wedge it filled and a stage the pie.
   */
  show(progress: DrillProgress, done?: "pass" | "stage"): void {
    if (done === undefined && this.#holding !== undefined) {
      this.#waiting = progress;

      return;
    }

    this.#draw(progress, done);

    if (done !== undefined) {
      // Anything queued behind an earlier flash is older than this.
      this.#waiting = undefined;
      clearTimeout(this.#holding);
      this.#holding = setTimeout(() => {
        const waiting = this.#waiting;

        this.#holding = undefined;
        this.#waiting = undefined;

        if (waiting !== undefined) {
          this.#draw(waiting, undefined);
        }
      }, pieFlashLength);
    }
  }

  #draw(progress: DrillProgress, done: "pass" | "stage" | undefined): void {
    this.#restartAnimations();
    this.#pies.forEach((pie, stage) => {
      const { state, filled } = pieOf(progress, stage);
      const current = done === "pass" && state === "current";

      pie.setAttribute("data-cb-xiezi-pie", state);
      pie.toggleAttribute(
        "data-cb-xiezi-flash",
        done === "stage" && stage === progress.stage - 1,
      );
      pie.toggleAttribute("data-cb-xiezi-rock", current);
      [...pie.children].forEach((wedge, at) => {
        wedge.toggleAttribute("data-cb-xiezi-filled", at < filled);
        wedge.toggleAttribute(
          "data-cb-xiezi-flash",
          current && at === filled - 1,
        );
      });
    });
  }

  /**
   * Take every animation off, settled, so that the same one set again on the
   * next pass plays again rather than carrying on as it was.
   */
  #restartAnimations(): void {
    for (const pie of this.#pies) {
      pie.removeAttribute("data-cb-xiezi-flash");
      pie.removeAttribute("data-cb-xiezi-rock");

      for (const wedge of pie.children) {
        wedge.removeAttribute("data-cb-xiezi-flash");
      }
    }

    this.#pies[0]?.getBoundingClientRect();
  }
}
